import { useMutation, useQueryClient } from "@tanstack/react-query";
import { expiringRelatedPartiesKeys } from "Api/Queries/Party/useExpiringRelatedPartiesQuery";
import { partyIdentificationImagesKeys } from "Api/Queries/Party/usePartyIdentificationImagesQuery";
import { getPartyQueryKey } from "Api/Queries/Party/usePartyQuery";
import { getVerificationsQueryKey } from "Api/Queries/Verifications/useVerificationsQuery";
import {
  type PostPartiesPublicIDUploadDocumentsFetchResponse,
  UploadIdentificationCommandResultStatus2,
  apiPost,
  getApiUrl,
  postPartiesPublicIDUploadDocumentsPath,
} from "Infrastructure/Api/Api";
import { type TFunction, Resources, useResource } from "Translations/Resources";
import { toast } from "sonner";

type MessageKey = Parameters<TFunction>[0];

type BaseParameters = {
  partyPublicID: string;
};

type Config<TParameters extends BaseParameters> = {
  buildFormData: (parameters: TParameters) => FormData;
  successMessage: MessageKey;
  errorMessage: MessageKey;
};

/**
 * Shared base for the party ID-document upload mutations (first / second
 * document). Both POST multipart form data to the same endpoint and differ
 * only in which form fields they send and which toast messages they show, so
 * each wrapper supplies a `buildFormData` and its messages.
 */
export const usePartyDocumentUploadMutation = <
  TParameters extends BaseParameters,
>({
  buildFormData,
  successMessage,
  errorMessage,
}: Config<TParameters>) => {
  const { t } = useResource();
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (parameters: TParameters) => {
      const result = (await apiPost(
        `${getApiUrl()}${postPartiesPublicIDUploadDocumentsPath(parameters.partyPublicID)}`,
        buildFormData(parameters),
        { headers: new Headers() },
      )) as PostPartiesPublicIDUploadDocumentsFetchResponse;

      if (
        result.data?.status !== UploadIdentificationCommandResultStatus2.Success
      ) {
        throw new Error(
          result.data?.status
            ? t(
                Resources.ApiStatuses.UploadIdentificationCommandResultStatus2[
                  result.data?.status
                ],
              )
            : t(Resources.ApiStatuses.UnknownError),
          { cause: { result } },
        );
      }
    },
    onSuccess: (_data, { partyPublicID }) => {
      queryClient.invalidateQueries({
        queryKey: getPartyQueryKey(partyPublicID),
      });
      queryClient.invalidateQueries({
        queryKey: partyIdentificationImagesKeys.byPublicId(partyPublicID),
      });
      queryClient.invalidateQueries({
        queryKey: getVerificationsQueryKey(),
      });
      queryClient.invalidateQueries({
        queryKey: expiringRelatedPartiesKeys.all,
      });
      toast.success(t(successMessage));
    },
    onError: () => toast.error(t(errorMessage)),
  });
};
